/**
 * Simpatico Front Page Scripts
 *
 * Handles scroll-triggered animations and interactions.
 *
 * @package Simpaticobike
 * @since 2.0.0
 */

import { initHeroSlideshow } from './hero-slideshow.mjs';

(function() {
	'use strict';

	/**
	 * Intersection Observer for fade-in animations
	 */
	function initScrollAnimations() {
		const fadeElements = document.querySelectorAll('.simpatico-fade-in');

		if (!fadeElements.length) return;

		// Check for reduced motion preference
		const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

		if (prefersReducedMotion) {
			// Show all elements immediately if user prefers reduced motion
			fadeElements.forEach(el => el.classList.add('is-visible'));
			return;
		}

		const observerOptions = {
			root: null,
			rootMargin: '0px 0px -50px 0px',
			threshold: 0.1
		};

		const observer = new IntersectionObserver((entries) => {
			entries.forEach(entry => {
				if (entry.isIntersecting) {
					entry.target.classList.add('is-visible');
					observer.unobserve(entry.target);
				}
			});
		}, observerOptions);

		fadeElements.forEach(el => observer.observe(el));
	}

	/**
	 * Smooth scroll for anchor links
	 */
	function initSmoothScroll() {
		document.querySelectorAll('a[href^="#"]').forEach(anchor => {
			anchor.addEventListener('click', function(e) {
				const targetId = this.getAttribute('href');

				if (targetId === '#') return;

				const target = document.querySelector(targetId);

				if (target) {
					e.preventDefault();
					target.scrollIntoView({
						behavior: 'smooth',
						block: 'start'
					});
				}
			});
		});
	}

	/**
	 * Parallax effect for hero section
	 */
	function initParallax() {
		const hero = document.querySelector('.simpatico-hero');
		const heroMedia = document.querySelector('.simpatico-hero__media');

		if (!hero || !heroMedia) return;

		// Check for reduced motion preference
		const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
		if (prefersReducedMotion) return;

		function updateParallax() {
			const heroRect = hero.getBoundingClientRect();
			const heroIsVisible = heroRect.bottom > 0 && heroRect.top < window.innerHeight;

			if (!heroIsVisible) {
				heroMedia.style.transform = '';
				return;
			}

			const yOffset = Math.max(0, -heroRect.top) * 0.4;
			heroMedia.style.transform = `translateY(${yOffset}px)`;
		}

		let ticking = false;
		function requestParallaxUpdate() {
			if (ticking) return;

			window.requestAnimationFrame(() => {
				updateParallax();
				ticking = false;
			});
			ticking = true;
		}

		window.addEventListener('scroll', () => {
			requestParallaxUpdate();
		}, { passive: true });

		requestParallaxUpdate();
		window.addEventListener('resize', requestParallaxUpdate, { passive: true });
		window.addEventListener('simpatico:hero:update-metrics', requestParallaxUpdate, { passive: true });
	}

		/**
		 * The poster is the initial experience. A single video source is attached
		 * only after that poster has painted and the browser is idle.
		 */
		function initVideoIntro() {
			const intro = document.querySelector('.simpatico-video-intro');
			if (!intro) return;

		if (intro.dataset.videoAvailable !== 'true') return;

			const desktopQuery = window.matchMedia('(min-width: 1024px)');
			const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

			const video = intro.querySelector('.simpatico-video-intro__video');
			const control = intro.querySelector('.simpatico-video-intro__control');
			const poster = intro.querySelector('.simpatico-video-intro__poster');
			if (!video || !control || !poster) return;

			video.muted = true;
			video.defaultMuted = true;
			video.playsInline = true;
			video.autoplay = true;

			const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
			const shouldKeepPoster = () => Boolean(
				reducedMotion.matches
				|| (connection && (connection.saveData || connection.effectiveType === '2g' || connection.effectiveType === 'slow-2g'))
			);
			const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
			const canUseWebm = () => !isSafari && video.canPlayType('video/webm; codecs="vp9"') !== '';
			const getVideoSource = () => {
				const prefix = desktopQuery.matches ? 'desktop' : 'mobile';
				const webmSource = video.dataset[`${prefix}WebmSrc`];
				const mp4Source = video.dataset[`${prefix}Mp4Src`];

				return canUseWebm() && webmSource ? webmSource : mp4Source;
			};

		const controlLabel = control.querySelector('[data-video-intro-control-label]');
		const controlText = control.querySelector('.simpatico-video-intro__control-text');
		const setControlState = (state, error = '') => {
			const labels = {
				idle: 'Background video is not loaded',
				loading: 'Loading background video',
				playing: 'Pause background video',
				paused: 'Play background video',
				blocked: 'Tap to play video',
				error: 'Retry background video',
			};
			const label = labels[state] || labels.paused;
			control.dataset.state = state;
			control.setAttribute('aria-pressed', String(state === 'playing'));
			control.setAttribute('aria-label', label);
			if (controlLabel) controlLabel.textContent = label;
			if (controlText) controlText.textContent = state === 'blocked' || state === 'error' ? label : '';
			if (error) intro.dataset.videoError = error;
		};

		const recordPlaybackFailure = (state, error) => {
			intro.dataset.videoState = state;
			setControlState(state, error);
		};
		const requestPlayback = () => {
			try {
				const playAttempt = video.play();
				if (playAttempt && typeof playAttempt.catch === 'function') {
					playAttempt.catch((error) => recordPlaybackFailure('blocked', error.name || 'playback'));
				}
			} catch (error) {
				recordPlaybackFailure('blocked', error.name || 'playback');
			}
		};

		video.addEventListener('loadstart', () => {
			firstFrameAt = 0;
			hiddenPosition = 0;
			intro.dataset.videoState = 'loading';
			delete intro.dataset.videoError;
			setControlState('loading');
		});
		video.addEventListener('loadedmetadata', () => { intro.dataset.videoState = 'metadata'; });
		// The poster stays until the video will run smoothly. A browser that can play
		// through within 1.5 s of its first frame is on a fast connection and starts at
		// once; that allows for a connection still ramping up, while slow loads take 3 s
		// or more. Otherwise the clip downloads behind the poster and starts from the
		// beginning once it is complete, so a slow connection does not stutter on screen.
		// A later canplaythrough is not trusted: Chromium assumes an even bitrate and
		// Firefox reports it when it suspends a paused download.
		let firstFrameAt = 0;
		let hiddenPosition = 0;
		const isRevealed = () => intro.classList.contains('is-video-ready');
		// A visitor who paused while the clip was still loading keeps it paused.
		const pausedByVisitor = () => control.dataset.state === 'paused';
		const isFullyBuffered = () => video.buffered.length > 0
			&& video.buffered.end(video.buffered.length - 1) >= video.duration - 0.1;
		const revealVideo = () => {
			if (!active || isRevealed()) return;
			if (video.currentTime > 0) video.currentTime = 0;
			intro.classList.add('is-video-ready');
			if (!pausedByVisitor()) requestPlayback();
		};
		video.addEventListener('canplay', () => {
			if (!active || isRevealed()) return;
			intro.dataset.videoState = 'ready';
			if (!firstFrameAt) firstFrameAt = performance.now();
		});
		video.addEventListener('canplaythrough', () => {
			if (isFullyBuffered() || !firstFrameAt || performance.now() - firstFrameAt < 1500) revealVideo();
		});
		const revealWhenBuffered = () => {
			if (isRevealed()) return;
			// A full pass behind the poster means the clip is there, whatever `buffered` reports.
			const wrapped = video.currentTime < hiddenPosition - 1;
			hiddenPosition = video.currentTime;
			if (wrapped || isFullyBuffered()) revealVideo();
		};
		video.addEventListener('progress', revealWhenBuffered);
		// Firefox completes its buffered range only after its last progress and suspend events.
		video.addEventListener('timeupdate', revealWhenBuffered);
		video.addEventListener('suspend', () => {
			if (isFullyBuffered()) {
				revealVideo();
			} else if (active && !isRevealed() && !pausedByVisitor()) {
				// The browser stopped fetching early; it resumes once playback is requested.
				requestPlayback();
			}
		});
		video.addEventListener('play', () => setControlState('playing'));
		video.addEventListener('playing', () => {
			intro.dataset.videoState = 'playing';
			delete intro.dataset.videoError;
			setControlState('playing');
		});
		video.addEventListener('pause', () => {
			if (active) {
				intro.dataset.videoState = 'paused';
				setControlState('paused');
			}
		});
		video.addEventListener('waiting', () => { intro.dataset.videoState = 'waiting'; });
		video.addEventListener('stalled', () => { intro.dataset.videoState = 'stalled'; });
		video.addEventListener('error', () => recordPlaybackFailure('error', `media-${video.error?.code || 'unknown'}`));

			control.addEventListener('click', () => {
				if (control.dataset.state === 'error' || control.dataset.state === 'blocked') {
					loadVideo();
					return;
				}

				if (video.paused) {
					// A visitor who presses play gets the video at once, even if it is still downloading.
					revealVideo();
					requestPlayback();
			} else {
				video.pause();
			}
		});

		let active = false;
		let idleCallbackId = 0;
		let idleTimeoutId = 0;
		let paintObserver = null;
		let fallbackPaintHandler = null;

		const cancelScheduledLoad = () => {
			if (idleCallbackId && window.cancelIdleCallback) window.cancelIdleCallback(idleCallbackId);
			if (idleTimeoutId) window.clearTimeout(idleTimeoutId);
			if (paintObserver) paintObserver.disconnect();
			if (fallbackPaintHandler) window.removeEventListener('load', fallbackPaintHandler);
			idleCallbackId = 0;
			idleTimeoutId = 0;
			paintObserver = null;
			fallbackPaintHandler = null;
		};
			const deactivateIntro = () => {
				active = false;
				cancelScheduledLoad();
				video.pause();
				video.autoplay = false;
				if (video.hasAttribute('src')) {
					video.removeAttribute('src');
					video.load();
				}
				intro.classList.remove('is-video-ready');
				setControlState('idle');
			};

			const loadVideo = () => {
				if (!active || shouldKeepPoster() || document.visibilityState !== 'visible') return;

				const videoSrc = getVideoSource();
				if (!videoSrc) {
					recordPlaybackFailure('error', 'source');
					return;
				}

				intro.classList.remove('is-video-ready');
				setControlState('loading');
				video.autoplay = true;
				video.preload = 'auto';
				video.src = videoSrc;
			video.load();
		};

		const queueAfterFirstContentfulPaint = (callback) => {
			const firstContentfulPaint = window.performance && window.performance.getEntriesByName
				? window.performance.getEntriesByName('first-contentful-paint')
				: [];

			if (firstContentfulPaint.length) {
				callback();
				return;
			}

			if (window.PerformanceObserver) {
				try {
					paintObserver = new PerformanceObserver((entries, observer) => {
						if (!entries.getEntries().some((entry) => entry.name === 'first-contentful-paint')) return;

						observer.disconnect();
						paintObserver = null;
						callback();
					});
					paintObserver.observe({ type: 'paint', buffered: true });
					return;
				} catch (error) {
					paintObserver = null;
				}
			}

			if (document.readyState === 'complete') {
				callback();
				return;
			}

			fallbackPaintHandler = () => {
				fallbackPaintHandler = null;
				callback();
			};
			window.addEventListener('load', fallbackPaintHandler, { once: true });
		};

		const queueVideoAfterPaint = () => {
			if (shouldKeepPoster()) {
					deactivateIntro();
					return;
			}

			active = true;
			queueAfterFirstContentfulPaint(() => {
				window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
					if (!active || document.visibilityState !== 'visible') return;
					if (window.requestIdleCallback) {
						idleCallbackId = window.requestIdleCallback(loadVideo, { timeout: 1500 });
					} else {
						idleTimeoutId = window.setTimeout(loadVideo, 0);
					}
				}));
			});
		};

			const scheduleVideo = () => {
				deactivateIntro();
				if (shouldKeepPoster()) return;

				if (poster.complete && poster.naturalWidth > 0) {
					queueVideoAfterPaint();
					return;
				}

				poster.addEventListener('load', queueVideoAfterPaint, { once: true });
			};
			const handleBreakpointChange = () => {
				scheduleVideo();
			};

			desktopQuery.addEventListener('change', handleBreakpointChange);
			reducedMotion.addEventListener('change', scheduleVideo);
			document.addEventListener('visibilitychange', () => {
				if (document.visibilityState === 'visible' && !video.getAttribute('src')) scheduleVideo();
			});
			scheduleVideo();
		}

		/** Load decorative below-the-fold backgrounds only as they approach view. */
		function initLazyBackgrounds() {
			const backgrounds = document.querySelectorAll('[data-simpatico-lazy-background]');
			if (!backgrounds.length) return;

			const loadBackground = (element) => {
				const url = element.dataset.simpaticoLazyBackground;
				if (!url) return;

				element.style.setProperty('--simpatico-image-testimonials', `url("${url}")`);
				element.removeAttribute('data-simpatico-lazy-background');
			};

			if (!('IntersectionObserver' in window)) {
				backgrounds.forEach(loadBackground);
				return;
			}

			const observer = new IntersectionObserver((entries) => {
				entries.forEach((entry) => {
					if (!entry.isIntersecting) return;
					loadBackground(entry.target);
					observer.unobserve(entry.target);
				});
			}, { rootMargin: '200px 0px' });

			backgrounds.forEach((element) => observer.observe(element));
		}

	/**
	 * Mobile loop for the models carousel
	 */
	function initModelsCarouselLoop() {
		const track = document.querySelector('.simpatico-bike-cards');
		if (!track) return;

		const cards = track.querySelectorAll('.simpatico-bike-card');
		if (cards.length < 2) return;

		const mobileQuery = window.matchMedia('(max-width: 767px)');
		let maxScroll = 0;

		function updateTrackMetrics() {
			maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
		}

		function loopIfNeeded() {
			if (!mobileQuery.matches) return;
			if (maxScroll <= 0) return;

			const threshold = 24;
			if (track.scrollLeft >= maxScroll - threshold) {
				track.scrollTo({ left: 0, behavior: 'smooth' });
			}
		}

		updateTrackMetrics();
		window.addEventListener('resize', updateTrackMetrics, { passive: true });

		track.addEventListener('touchend', loopIfNeeded, { passive: true });
		track.addEventListener('mouseup', loopIfNeeded);
	}

	/**
	 * Initialize all front page functionality
	 */
	function init() {
		initScrollAnimations();
		initSmoothScroll();
			initParallax();
			initVideoIntro();
			initLazyBackgrounds();
			initHeroSlideshow();
		initModelsCarouselLoop();
	}

	// Run on DOM ready
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}

})();
